/**
 * Acquisition P7 (docs/plans/2026-10-11_acquisition.md; the Go side is platform-core/internal/acquisition/stations.go
 * and learning.go): what the org's applied acquisition studies collect at one station, and the corrections persons made
 * the same way at several plants.
 *
 *   GET /v1/twin/acquisition/stations/{station_id}/points   a station's points and its gaps (any member)
 *   GET /v1/platform/acquisition/learning?min_orgs=          proposals for the foundry pack (platform admins)
 *
 * A station's points are, of each source, the latest applied study's rows a person accepted or edited whose final
 * target names the station, kept or dropped (as the study's page says Keep or Drop: an apply writes only vocabulary
 * bindings and learned mappings, so nothing is collected yet); its gaps are the required signals of the classes its
 * equipment and its points belong to that no kept point covers. A learning proposal is a parameter correction made at
 * several organisations: it names no org and carries no value, and a person turns it into a pack PR.
 *
 * Kept apart from ./acquisition (the sources and studies) so the two slices grow without touching one file.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { useOrg } from "@/lib/org-context";
import { statusOf } from "./error";

/** How a point is read: per cycle (when its trigger steps), on change, or every `running` s while running, `idle` while idle. */
export interface PointRate {
  per_cycle?: boolean;
  on_event?: boolean;
  running?: number;
  idle?: number;
}

export type PointSourceKind = "tag_list" | "data_export" | "probe_window" | "agent_kind";

/** One point an applied study put at the station: the person's edit, else the plan's proposal they accepted. */
export interface StationPoint {
  source: { id: string; name: string; kind: PointSourceKind };
  /** The latest applied study of the source. */
  study_id: string;
  applied_at: string;
  point_key: string;
  name: string;
  path?: string;
  address?: string;
  unit?: string;
  decision: "accept" | "edit";
  entity_id?: string;
  /** The acquisition profile class, and the profile's words for it. */
  class?: string;
  class_label?: string;
  signal?: string;
  signal_label?: string;
  /** The pack parameter (canonical name), and the pack's label for it. */
  param?: string;
  param_label?: string;
  scale?: number;
  /** False for a point the row dropped: listed, not kept. */
  keep: boolean;
  rate: PointRate;
  deadband?: number;
  heartbeat?: number;
  /** The name of the counter point whose step reads this per cycle point. */
  trigger?: string;
}

/** A required signal of a class the station holds equipment of, or has a point in, that no kept point of it maps to. */
export interface StationGap {
  class: string;
  class_label: string;
  signal: string;
  label: string;
  param?: string;
  words: string;
  /** The profile's words on why the signal matters. */
  why?: string;
}

export interface StationPoints {
  station_id: string;
  /** At most 5,000, sorted by class (the profiles' order, a point of no class last) then name. */
  points: StationPoint[];
  /** Every point that names the station. */
  total: number;
  /** Only the first 5,000 points are listed. */
  truncated: boolean;
  /** More than 50 applied studies named the station and only the latest 50 were read: points and gaps may be incomplete. */
  studies_truncated: boolean;
  gaps: StationGap[];
}

/** One parameter correction made the same way at several organisations: what the pack would need, never an org. */
export interface LearningProposal {
  fold: string;
  /** The pack parameter the points were set to (canonical name), and the pack's label for it. */
  param: string;
  param_label?: string;
  /** How many organisations made it. */
  orgs: number;
  need: "alias" | "none";
  words: string;
}

export interface AcquisitionLearning {
  min_orgs: number;
  proposals: LearningProposal[];
  corrections: number;
  truncated: boolean;
}

const enc = encodeURIComponent;

export const acquisitionPointsApi = {
  stationPoints: (stationId: string) =>
    api.get<StationPoints>(`/v1/twin/acquisition/stations/${enc(stationId)}/points`),
  learning: (minOrgs: number) => api.get<AcquisitionLearning>(`/v1/platform/acquisition/learning?min_orgs=${minOrgs}`),
};

export const stationPointsKey = (stationId: string) => ["acquisition-station-points", stationId] as const;
export const acquisitionLearningKey = (minOrgs: number) => ["acquisition-learning", minOrgs] as const;

const isLiveApiFlag = import.meta.env.VITE_USE_LIVE_API === "true";

/** A refused read (a 4xx: a vendor overlay, the pack off, no such station) is not tried again. */
function retryRead(failures: number, err: unknown): boolean {
  const status = statusOf(err);
  return !(status !== undefined && status >= 400 && status < 500) && failures < 2;
}

/** The station's points and gaps. Live reads need the build flag, a signed-in session and a resolved org. */
export function useStationPointsQuery(stationId: string | null | undefined) {
  const { isAuthenticated } = useAuth();
  const { activeOrgId } = useOrg();
  const live = isLiveApiFlag && isAuthenticated && activeOrgId !== null;
  return useQuery({
    queryKey: stationPointsKey(stationId ?? ""),
    queryFn: () => acquisitionPointsApi.stationPoints(stationId ?? ""),
    enabled: live && !!stationId,
    staleTime: 60_000,
    retry: retryRead,
  });
}

/** The cross-plant learning proposals (the platform console's; platform admins only). */
export function useAcquisitionLearningQuery(minOrgs: number, enabled = true) {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: acquisitionLearningKey(minOrgs),
    queryFn: () => acquisitionPointsApi.learning(minOrgs),
    enabled: isLiveApiFlag && isAuthenticated && enabled,
    staleTime: 60_000,
    retry: retryRead,
  });
}
